import React, { useEffect, useState } from "react";
import {
  Button,
  Spin,
  Switch,
  Typography,
  notification,
} from "antd";

import {
  BarChartOutlined,
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  LockOutlined,
  MailOutlined,
  MessageOutlined,
  MobileOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";

import { useTranslation } from "react-i18next";

import "./index.css";

import CommonLayout from "../../CommonLayout";

import {
  getUserNotificationPrefrances,
  updateUserNotificationPrefrances,
  NotificationPreferences as NotificationPreferencesType,
} from "../../../../Services/UserProfileServices";

type SummaryFrequency =
  | "MONTHLY"
  | "FORTNIGHTLY"
  | "WEEKLY"
  | "DAILY";

const NotificationPreferences: React.FC = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [preferences, setPreferences] =
    useState<NotificationPreferencesType>({
      inAppEnabled: true,
      smsEnabled: true,
      emailEnabled: true,
      accountLinkingEnabled: true,
      consentApprovedEnabled: true,
      consentRevokedEnabled: true,
      consentPauseResumeEnabled: true,
      dataFetchEnabled: true,
      fiuSummaryFrequency: "MONTHLY",
    });

  useEffect(() => {
    loadNotificationPreferences();
  }, []);

  const loadNotificationPreferences = async () => {
    try {
      setLoading(true);

      const response = await getUserNotificationPrefrances();

      if (
        response?.data?.status === "SUCCESS" &&
        response?.data?.data
      ) {
        setPreferences(response.data.data);
        setHasChanges(false);
      }
    } catch (error) {
      console.error(
        "Failed to fetch notification preferences:",
        error
      );

      notification.error({
        message: t("msgSomethingWentWrong"),
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = (
    key: keyof NotificationPreferencesType,
    value: boolean
  ) => {
    setPreferences((previous) => ({
      ...previous,
      [key]: value,
    }));

    setHasChanges(true);
  };

  const updateFrequency = (frequency: SummaryFrequency) => {
    setPreferences((previous) => ({
      ...previous,
      fiuSummaryFrequency: frequency,
    }));

    setHasChanges(true);
  };

  const handleUpdatePreferences = async () => {
    if (!hasChanges) {
      return;
    }

    try {
      setSaving(true);

      const payload: NotificationPreferencesType = {
        inAppEnabled: preferences.inAppEnabled,
        smsEnabled: preferences.smsEnabled,
        emailEnabled: preferences.emailEnabled,
        accountLinkingEnabled:
          preferences.accountLinkingEnabled,
        consentApprovedEnabled:
          preferences.consentApprovedEnabled,
        consentRevokedEnabled:
          preferences.consentRevokedEnabled,
        consentPauseResumeEnabled:
          preferences.consentPauseResumeEnabled,
        dataFetchEnabled:
          preferences.dataFetchEnabled,
        fiuSummaryFrequency:
          preferences.fiuSummaryFrequency,
      };

      const response =
        await updateUserNotificationPrefrances(payload);

      if (
        response?.data?.status === "SUCCESS"
      ) {
        if (response?.data?.data) {
          setPreferences(response.data.data);
        }

        setHasChanges(false);

        notification.success({
          message:
            response?.data?.message ||
            t("msgNotificationPreferencesUpdated"),
        });
      } else {
        notification.error({
          message:
            response?.data?.message ||
            t("msgSomethingWentWrong"),
        });
      }
    } catch (error) {
      console.error(
        "Failed to update notification preferences:",
        error
      );

      notification.error({
        message: t("msgSomethingWentWrong"),
      });
    } finally {
      setSaving(false);
    }
  };

  const renderNotificationRow = (
    icon: React.ReactNode,
    title: string,
    description: string,
    checked: boolean,
    onChange: (checked: boolean) => void
  ) => {
    return (
      <div className="notificationPreferenceRow">
        <div className="notificationPreferenceLeft">
          <div className="notificationPreferenceIcon">
            {icon}
          </div>

          <div className="notificationPreferenceContent">
            <Typography.Text className="notificationPreferenceTitle">
              {title}
            </Typography.Text>

            <Typography.Text className="notificationPreferenceDescription">
              {description}
            </Typography.Text>
          </div>
        </div>

        <Switch
          checked={checked}
          onChange={onChange}
          disabled={saving}
        />
      </div>
    );
  };

  return (
    <CommonLayout
      title={t("lblNotificationPreferences")}
      showBackButton={true}
    >
      <div className="notificationPreferencesRoot">
        {loading ? (
          <div className="notificationPreferencesLoader">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* Intro */}
            <div className="notificationPreferencesIntro">
              <div className="notificationIntroIcon">
                <InfoCircleOutlined />
              </div>

              <div className="notificationIntroContent">
                <Typography.Text className="notificationIntroTitle">
                  {t("lblCustomizeNotificationPreferences")}
                </Typography.Text>

                <Typography.Text className="notificationIntroDescription">
                  {t("lblChooseHowYouReceiveNotifications")}
                </Typography.Text>
              </div>
            </div>

            {/* =====================================================
                NOTIFICATION CHANNELS
               ===================================================== */}

            <div className="notificationSectionHeader">
              <div className="notificationSectionHeaderIcon">
                <BellOutlined />
              </div>

              <div>
                <Typography.Title level={4}>
                  {t("lblNotificationChannels")}
                </Typography.Title>

                <Typography.Text>
                  {t("lblChooseYourPreferredNotificationChannels")}
                </Typography.Text>
              </div>
            </div>

            <div className="notificationPreferencesCard">
              {renderNotificationRow(
                <MobileOutlined />,
                t("lblInAppNotifications"),
                t("lblReceiveNotificationsWithinApp"),
                preferences.inAppEnabled,
                (checked) =>
                  updatePreference(
                    "inAppEnabled",
                    checked
                  )
              )}

              {renderNotificationRow(
                <MessageOutlined />,
                t("lblSmsNotifications"),
                t("lblReceiveNotificationsViaSms"),
                preferences.smsEnabled,
                (checked) =>
                  updatePreference(
                    "smsEnabled",
                    checked
                  )
              )}

              {renderNotificationRow(
                <MailOutlined />,
                t("lblEmailNotifications"),
                t("lblReceiveNotificationsViaEmail"),
                preferences.emailEnabled,
                (checked) =>
                  updatePreference(
                    "emailEnabled",
                    checked
                  )
              )}
            </div>

            {/* =====================================================
                REAL-TIME NOTIFICATIONS
               ===================================================== */}

            <div className="notificationSectionHeader">
              <div className="notificationSectionHeaderIcon">
                <BellOutlined />
              </div>

              <div>
                <Typography.Title level={4}>
                  {t("lblRealtimeNotifications")}
                </Typography.Title>

                <Typography.Text>
                  {t("lblChooseEventsYouWantToBeNotifiedAbout")}
                </Typography.Text>
              </div>
            </div>

            <div className="notificationPreferencesCard">
              {renderNotificationRow(
                <LinkOutlined />,
                t("lblAccountLinking"),
                t("lblNotifyWhenFinancialAccountLinked"),
                preferences.accountLinkingEnabled,
                (checked) =>
                  updatePreference(
                    "accountLinkingEnabled",
                    checked
                  )
              )}

              {renderNotificationRow(
                <CheckCircleOutlined />,
                t("lblConsentApproved"),
                t("lblNotifyWhenConsentApproved"),
                preferences.consentApprovedEnabled,
                (checked) =>
                  updatePreference(
                    "consentApprovedEnabled",
                    checked
                  )
              )}

              {renderNotificationRow(
                <CloseCircleOutlined />,
                t("lblConsentRevoked"),
                t("lblNotifyWhenConsentRevoked"),
                preferences.consentRevokedEnabled,
                (checked) =>
                  updatePreference(
                    "consentRevokedEnabled",
                    checked
                  )
              )}

              {renderNotificationRow(
                <PauseCircleOutlined />,
                t("lblConsentPausedResumed"),
                t("lblNotifyWhenConsentPausedResumed"),
                preferences.consentPauseResumeEnabled,
                (checked) =>
                  updatePreference(
                    "consentPauseResumeEnabled",
                    checked
                  )
              )}

              {renderNotificationRow(
                <DatabaseOutlined />,
                t("lblSuccessfulDataFetch"),
                t("lblNotifyWhenDataSuccessfullyFetched"),
                preferences.dataFetchEnabled,
                (checked) =>
                  updatePreference(
                    "dataFetchEnabled",
                    checked
                  )
              )}
            </div>

            {/* =====================================================
                SCHEDULED SUMMARIES
               ===================================================== */}

            <div className="notificationSectionHeader">
              <div className="notificationSectionHeaderIcon">
                <CalendarOutlined />
              </div>

              <div>
                <Typography.Title level={4}>
                  {t("lblScheduledSummaries")}
                </Typography.Title>

                <Typography.Text>
                  {t("lblManageYourScheduledSummaryFrequency")}
                </Typography.Text>
              </div>
            </div>

            {/* Monthly AA Summary */}
            <div className="mandatorySummaryCard">
              <div className="summaryCardIcon mandatoryIcon">
                <MailOutlined />
              </div>

              <div className="summaryCardContent">
                <div className="summaryCardTitleRow">
                  <Typography.Text className="summaryCardTitle">
                    {t("lblMonthlyAaSummary")}
                  </Typography.Text>

                  <span className="mandatoryBadge">
                    {t("lblMandatory")}
                  </span>
                </div>

                <Typography.Text className="summaryCardDescription">
                  {t(
                    "lblActiveConsentDetailsAndDataFetchSummary"
                  )}
                </Typography.Text>

                <Typography.Text className="summaryCardHint">
                  {t(
                    "lblSentOnceAMonthViaEnabledChannels"
                  )}
                </Typography.Text>
              </div>

              <div className="summaryLock">
                <LockOutlined />
              </div>
            </div>

            {/* FI-U Data Fetch Summary */}
            <div className="optionalSummaryCard">
              <div className="optionalSummaryHeader">
                <div className="summaryCardIcon">
                  <BarChartOutlined />
                </div>

                <div className="summaryCardContent">
                  <div className="summaryCardTitleRow">
                    <Typography.Text className="summaryCardTitle">
                      {t("lblFiuDataFetchSummary")}
                    </Typography.Text>

                    <span className="optionalBadge">
                      {t("lblOptional")}
                    </span>
                  </div>

                  <Typography.Text className="summaryCardDescription">
                    {t(
                      "lblChooseHowOftenDataFetchSummary"
                    )}
                  </Typography.Text>
                </div>
              </div>

              <div className="frequencySelector">
                {[
                  {
                    value: "MONTHLY" as SummaryFrequency,
                    label: t("lblMonthly"),
                  },
                  {
                    value: "FORTNIGHTLY" as SummaryFrequency,
                    label: t("lblFortnightly"),
                  },
                  {
                    value: "WEEKLY" as SummaryFrequency,
                    label: t("lblWeekly"),
                  },
                  {
                    value: "DAILY" as SummaryFrequency,
                    label: t("lblDaily"),
                  },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={
                      preferences.fiuSummaryFrequency ===
                      option.value
                        ? "frequencyOption active"
                        : "frequencyOption"
                    }
                    disabled={saving}
                    onClick={() =>
                      updateFrequency(option.value)
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Information */}
            <div className="notificationInfoBox">
              <InfoCircleOutlined />

              <Typography.Text>
                {t("lblNotificationPreferencesInfo")}
              </Typography.Text>
            </div>

            {/* Update */}
            <div className="notificationPreferencesActions">
              <div className="notificationSaveStatus">
                {hasChanges ? (
                  <>
                    <span className="unsavedDot" />
                    <span>
                      {t("lblUnsavedChanges")}
                    </span>
                  </>
                ) : (
                  <>
                    <CheckCircleOutlined />
                    <span>
                      {t("lblAllChangesSaved")}
                    </span>
                  </>
                )}
              </div>

              <Button
                type="primary"
                loading={saving}
                disabled={!hasChanges}
                onClick={handleUpdatePreferences}
                className="updatePreferencesButton"
              >
                {t("lblUpdatePreferences")}
              </Button>
            </div>
          </>
        )}
      </div>
    </CommonLayout>
  );
};

export default NotificationPreferences;